import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import loadable from '@loadable/component';
import { useRemoveSplashLoader } from '@minecraft.utils';
import Loader from '@minecraft.loader';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import routeHelper from './helper';

const ClaimAdditionalAccount = loadable(
  () => import(/* webpackChunkName: "onboarding-login" */ 'src/routes/claim-additional-account')
);
const AccountRecovery = loadable(
  () => import(/* webpackChunkName: "onboarding-acc-recovery" */ 'src/routes/account-recovery')
);
const ArtistIdentification = loadable(
  () => import(/* webpackChunkName: "onboarding-artist-ident" */ 'src/routes/artist-identification')
);
const ContactSupport = loadable(
  () => import(/* webpackChunkName: "onboarding-contact-support" */ 'src/routes/contact-support')
);
const CreateAccount = loadable(
  () => import(/* webpackChunkName: "onboarding-create-acc" */ 'src/routes/create-account')
);
const Disambiguation = loadable(
  () => import(/* webpackChunkName: "onboarding-disambiguation" */ 'src/routes/disambiguation')
);
const FoundProfiles = loadable(
  () => import(/* webpackChunkName: "onboarding-found-profiles" */ 'components/FoundProfiles')
);
const MapProfiles = loadable(() => import(/* webpackChunkName: "onboarding-map-profiles" */ 'src/routes/map-profiles'));
const MaxClaimsReached = loadable(
  () => import(/* webpackChunkName: "onboarding-max-claims" */ 'src/routes/max-claims-reached')
);
const Onboarding = loadable(() => import(/* webpackChunkName: "onboarding-onboarding" */ 'src/routes/onboarding'));
const OnboardingAdditionalAccount = loadable(
  () => import(/* webpackChunkName: "onboarding-onboarding" */ 'src/routes/onboarding-additional-account')
);
const OrganizationClaim = loadable(
  () => import(/* webpackChunkName: "onboarding-org-claim" */ 'src/routes/organization-claim')
);
const PersonalProfile = loadable(
  () => import(/* webpackChunkName: "onboarding-pers-profile" */ 'src/routes/personal-profile')
);
const ResetEmail = loadable(() => import(/* webpackChunkName: "onboarding-reset-email" */ 'src/routes/reset-email'));
const SelectPersonalProfile = loadable(
  () => import(/* webpackChunkName: "onboarding-select-pers-profile" */ 'src/routes/select-personal-profile')
);
const StubPage = loadable(() => import(/* webpackChunkName: "onboarding-stub" */ 'src/routes/stub-page'));
const UnableToRecover = loadable(
  () => import(/* webpackChunkName: "onboarding-unable-2-recover" */ 'src/routes/unable-to-recover')
);
const VerifyEmail = loadable(() => import(/* webpackChunkName: "onboarding-verify-email" */ 'src/routes/verify-email'));
const AdditionalClaimWelcome = loadable(
  () => import(/* webpackChunkName: "onboarding-additional-claim-welcome" */ 'components/AdditionalClaimWelcome')
);
const ClaimSuccessPage = loadable(() => import(/* webpackChunkName: "onboarding-claim-success" */ './claim-success'));
const Maintenance = loadable(
  () => import(/* webpackChunkName: "onboarding-maintencance" */ 'src/routes/splash-screens/maintenance')
);

const ProjectCreatorInformation = loadable(
  (/* webpackChunkName: "onboarding-project-creator-information" */) => import('src/routes/project-creator-information')
);

const NotFound = loadable(() => import(/* webpackChunkName: "onboarding-not-found" */ '@blocs.not-found'), {
  resolveComponent: (components) => components.NotFound,
});

export const Routes = (): JSX.Element => {
  const isLegacyOnboardingDeprecated = useFeatureExperiment(FEATURE_FLAGS.WEB_ONBOARDING_DEPRECATION);
  useRemoveSplashLoader();

  return (
    <Switch>
      {isLegacyOnboardingDeprecated && <Redirect to="/login" />}
      <Route exact path={routeHelper.maintenance.href()}>
        <Maintenance fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.claimAdditionalAccount.href()}>
        <ClaimAdditionalAccount fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.accountRecovery.href()}>
        <AccountRecovery fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.artistIdentification.href()}>
        <ArtistIdentification fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.contactSupport.href()}>
        <ContactSupport fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.createAccount.href()}>
        <CreateAccount fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.disambiguation.href()}>
        <Disambiguation fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.foundProfiles.href()}>
        <FoundProfiles fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.mapProfiles.href()}>
        <MapProfiles fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.maxClaimsReached.href()}>
        <MaxClaimsReached fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.onboarding.href()}>
        <Onboarding fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.onboardingAdditionalAccount.href()}>
        <OnboardingAdditionalAccount fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.organizationClaim.href()}>
        <OrganizationClaim fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.personalProfile.href()}>
        <PersonalProfile fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.resetEmail.href()}>
        <ResetEmail fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.selectPersonalProfile.href()}>
        <SelectPersonalProfile fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.stubPage.href()}>
        <StubPage fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.unableToRecover.href()}>
        <UnableToRecover fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.verifiedEmail.href()}>
        <VerifyEmail fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.additionalClaimWelcome.href()}>
        <AdditionalClaimWelcome fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.claimSuccess.href()}>
        <ClaimSuccessPage fallback={<Loader />} />
      </Route>
      <Route exact path={routeHelper.projectCreatorInformation.href()}>
        <ProjectCreatorInformation fallback={<Loader />} />
      </Route>
      <Route path="*">
        <NotFound fallback={<Loader />} />
      </Route>
    </Switch>
  );
};

export default Routes;
